/**
 * ResearchCentreCards Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A sophisticated card-based interface for displaying research articles with advanced features.
 * Provides rich visual presentation with search highlighting and professional modal integration.
 * 
 * CORE FEATURES:
 * - Responsive card grid layout with professional styling
 * - Advanced search query highlighting with HTML injection
 * - Dynamic category color coding using IFIA brand colors
 * - Tooltip integration for content previews and enhanced UX
 * - Modal system for detailed article viewing
 * - Author and company information display
 * - Date-based article organization with calendar icons
 * 
 * VISUAL DESIGN:
 * - Professional card layout with consistent spacing
 * - IFIA brand color system for category tags
 * - Responsive grid that adapts to different screen sizes
 * - Loading states and empty state handling
 * - Rich typography with proper text truncation
 * - Icon-based information presentation
 * 
 * SEARCH INTEGRATION:
 * - Real-time search query highlighting in titles and summaries
 * - HTML injection for highlighted text with custom CSS classes
 * - Robust text processing with URL removal and normalization
 * - Context-aware highlighting that preserves formatting
 * 
 * CATEGORY SYSTEM:
 * - Advanced category color mapping system
 * - IFIA brand color integration (green, dark-green, orange, blue, purple)
 * - Fallback color rotation for unmapped categories
 * - Hash-based color assignment for consistency
 * - Category tag display with visual hierarchy
 * 
 * MODAL INTEGRATION:
 * - Professional modal system for article details
 * - State management for modal visibility
 * - Article data passing with image integration
 * - Fullscreen support for enhanced reading experience
 * 
 * TECHNICAL IMPLEMENTATION:
 * - React Hooks for state management
 * - Text utility integration for content processing
 * - Tooltip system with Tippy.js
 * - Responsive design with CSS Grid
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of research article objects
 * @param {string} props.data[].id - Unique article identifier
 * @param {string} props.data[].title - Article title
 * @param {string} props.data[].displaySummary - Article summary/description
 * @param {string} props.data[].issueDate - Publication date (DD/MM/YYYY format)
 * @param {string} props.data[].weeklyCategory - Primary category classification
 * @param {string} props.data[].strategicCategory - Strategic category classification
 * @param {string} props.data[].author - Article author name
 * @param {string} props.data[].submittedAuthor - Submitted author name
 * @param {string} props.data[].submittedCompany - Company/organization name
 * @param {number} props.visibleCount - Number of cards to display (default: 6)
 * @param {string} props.searchQuery - Search query for highlighting (default: '')
 * @returns {JSX.Element} Research articles card grid interface
 */
import React, { useEffect, useState } from 'react';
import './ResearchCentreCards.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { truncateText, removeURLs, replaceCss, normalizeText } from '../../utils/textUtils';
import Modal from '../../modals/NewsModal';

/**
 * ResearchCentreCards Component
 * 
 * Professional card-based research article display with search highlighting and modal integration.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Research articles array
 * @param {number} props.visibleCount - Display count limit
 * @param {string} props.searchQuery - Search query for highlighting
 * @returns {JSX.Element} Research articles card interface
 */
const ResearchCentreCards = ({ data, visibleCount = 6, searchQuery = '' }) => {
    const [showModal, setShowModal] = useState(false);
    const [activeArticle, setActiveArticle] = useState(null);

    // Get category color class matching UnifiedNews color scheme
    const getCategoryColor = (category) => {
        if (!category) return 'ifia-green'; // Default to IFIA green
        
        // Use the same category mapping as UnifiedNews (replaceCss creates class names like "AdvocacyRegulatory")
        const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "");
        
        const colorMap = {
            // Exact matches from UnifiedNews
            'Intro': 'ifia-dark-green',
            'AdvocacyRegulatory': 'ifia-green',
            'Events': 'ifia-orange',
            'EngagementsCommunications': 'ifia-orange',
            'EngagementCommunications': 'ifia-purple',
            'CapabilityInnovation': 'ifia-dark-green',
            'WorkingGroupUpdates': 'ifia-blue',
            
            // Additional category mappings using IFIA colors
            'Tax': 'ifia-green',
            'ESG': 'ifia-dark-green',
            'FinancialLiteracy': 'ifia-orange',
            'Digitalisation': 'ifia-blue',
            'Product': 'ifia-purple',
            'LegalStructures': 'ifia-green',
            'Innovation': 'ifia-dark-green',
            'Strategy': 'ifia-blue',
            'Regulatory': 'ifia-green',
            'MarketUpdates': 'ifia-orange',
            'IndustryNews': 'ifia-purple',
            
            // Fallback patterns
            'Legal': 'ifia-green',
            'Market': 'ifia-orange',
            'Industry': 'ifia-purple',
            'Financial': 'ifia-orange',
            'Digital': 'ifia-blue',
            'Compliance': 'ifia-green',
            'Policy': 'ifia-green',
            'Technology': 'ifia-blue',
            'Sustainability': 'ifia-dark-green',
            'Research': 'ifia-purple'
        };
        
        // First try exact match
        if (colorMap[normalizedCategory]) {
            return colorMap[normalizedCategory];
        }
        
        // Then try partial matches
        const normalizedLower = normalizedCategory.toLowerCase();
        const matchedKey = Object.keys(colorMap).find(key => 
            normalizedLower.includes(key.toLowerCase()) || key.toLowerCase().includes(normalizedLower)
        );
        
        if (matchedKey) {
            return colorMap[matchedKey];
        }
        
        // Fallback to IFIA color rotation
        const ifiaColors = ['ifia-green', 'ifia-dark-green', 'ifia-orange', 'ifia-blue', 'ifia-purple'];
        const hash = normalizedCategory.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        
        return ifiaColors[Math.abs(hash) % ifiaColors.length];
    };

    // Get category icon matching UnifiedNews system
    const getCategoryIcon = (category) => {
        if (!category) return 'fa-tag'; // Default icon
        
        // Use the same category mapping as UnifiedNews (replaceCss creates class names like "AdvocacyRegulatory")
        const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "");
        
        const iconMap = {
            // Exact matches from UnifiedNews
            'Intro': 'fa-star',
            'AdvocacyRegulatory': 'fa-gavel',
            'Events': 'fa-calendar-alt',
            'EngagementsCommunications': 'fa-comments',
            'EngagementCommunications': 'fa-comments',
            'CapabilityInnovation': 'fa-lightbulb',
            'WorkingGroupUpdates': 'fa-users',
            
            // Additional category mappings
            'Tax': 'fa-calculator',
            'ESG': 'fa-leaf',
            'FinancialLiteracy': 'fa-graduation-cap',
            'Digitalisation': 'fa-digital-tachograph',
            'Product': 'fa-box',
            'LegalStructures': 'fa-balance-scale',
            'Innovation': 'fa-lightbulb',
            'Strategy': 'fa-chess',
            'Regulatory': 'fa-gavel',
            'MarketUpdates': 'fa-chart-line',
            'IndustryNews': 'fa-industry',
            
            // Fallback patterns
            'Legal': 'fa-balance-scale',
            'Market': 'fa-chart-line',
            'Industry': 'fa-industry',
            'Financial': 'fa-euro-sign',
            'Digital': 'fa-digital-tachograph',
            'Compliance': 'fa-shield-alt',
            'Policy': 'fa-file-contract',
            'Technology': 'fa-microchip',
            'Sustainability': 'fa-leaf',
            'Research': 'fa-search'
        };
        
        // First try exact match
        if (iconMap[normalizedCategory]) {
            return iconMap[normalizedCategory];
        }
        
        // Then try partial matches
        const normalizedLower = normalizedCategory.toLowerCase();
        const matchedKey = Object.keys(iconMap).find(key => 
            normalizedLower.includes(key.toLowerCase()) || key.toLowerCase().includes(normalizedLower)
        );
        
        if (matchedKey) {
            return iconMap[matchedKey];
        }
        
        // Fallback to default tag icon
        return 'fa-tag';
    };

    const handleShow = (itemData) => {
        setActiveArticle(itemData);
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setActiveArticle(null);
    };

    return (
        <section id="ResearchCentreCards" className="research-cards-section">
            {data.length === 0 ? (
                <div className="no-results">
                    <div className="no-results-content">
                        <i className="fas fa-search"></i>
                        <h3>No research articles found</h3>
                        <p>Try adjusting your search criteria or filters to see more results.</p>
                    </div>
                </div>
            ) : (
                <div className="research-cards-container">
                    <div className="research-cards-grid">
                        {data.map((item) => (
                            <div
                                key={item.id}
                                className="research-card"
                                onClick={() => handleShow(item)}
                                style={{ cursor: 'pointer' }}
                            >
                                {(item.displayDate || item.issueDate) && (
                                    <div className="research-card-date-header">
                                        <div className="research-icon">
                                        <i className="fas fa-newspaper"></i>
                                        </div>
                                        <div className="issue-date-top">
                                            <i className="fas fa-calendar-alt"></i>
                                            <span>{item.displayDate || item.issueDate}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="research-card-content">
                                    <Tippy content={truncateText(normalizeText(item.title), 120)}>
                                        <h3 className="research-card-title">
                                            <span className="title-text">
                                            {searchQuery.trim() ? (
                                                <span dangerouslySetInnerHTML={{ 
                                                        __html: truncateText(normalizeText(item.title), 65).replace(
                                                        new RegExp(searchQuery.trim(), 'gi'), 
                                                        match => `<span class="highlight-match">${match}</span>`
                                                    )
                                                }} />
                                            ) : (
                                                    truncateText(normalizeText(item.title), 65)
                                            )}
                                            </span>
                                        </h3>
                                    </Tippy>

                                    {item.displaySummary && item.displaySummary.trim() && (
                                        <div className="research-card-summary">
                                            <Tippy content={truncateText(removeURLs(normalizeText(item.displaySummary)), 250)}>
                                                <p>
                                                    {searchQuery.trim() ? (
                                                        <span dangerouslySetInnerHTML={{ 
                                                            __html: truncateText(removeURLs(normalizeText(item.displaySummary)), 180).replace(
                                                                new RegExp(searchQuery.trim(), 'gi'), 
                                                                match => `<span class="highlight-match">${match}</span>`
                                                            )
                                                        }} />
                                                    ) : (
                                                        truncateText(removeURLs(normalizeText(item.displaySummary)), 180)
                                                    )}
                                                </p>
                                            </Tippy>
                                        </div>
                                    )}
                                </div>

                                <div className="research-card-footer">
                                    {/* Author and Company Info - Only for Featured & Submitted Articles */}
                                    {item.sourceType === 'featured' && (item.author || item.submittedAuthor || item.submittedCompany) && (
                                        <div className="research-card-author-info">
                                            {(item.author || item.submittedAuthor) && (
                                                <div className="author-info">
                                                    <i className="fas fa-user"></i>
                                                    <span>{item.author || item.submittedAuthor}</span>
                                                </div>
                                            )}
                                            {item.submittedCompany && (
                                                <div className="company-info">
                                                    <i className="fas fa-building"></i>
                                                    <span>{item.submittedCompany}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="research-card-tags">
                                        {item.sourceType && (
                                            <Tippy content={item.sourceType === 'news' ? 'News' : 'Featured & Submitted Articles'}>
                                                <span className={`research-tag source-tag research-tag-${item.sourceType === 'news' ? 'ifia-blue' : 'ifia-purple'}`}>
                                                    <i className={`fas ${item.sourceType === 'news' ? 'fa-newspaper' : 'fa-star'}`}></i>
                                                    {item.sourceType === 'news' ? 'News' : 'Featured'}
                                                </span>
                                            </Tippy>
                                        )}
                                        {item.weeklyCategory && (
                                            <Tippy content={item.weeklyCategory}>
                                                <span className={`research-tag category-tag research-tag-${getCategoryColor(item.weeklyCategory)}`}>
                                                    <i className={`fas ${getCategoryIcon(item.weeklyCategory)}`}></i>
                                                    {item.weeklyCategory}
                                            </span>
                                            </Tippy>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {activeArticle && showModal && (
                <Modal
                    data={activeArticle}
                    show={showModal}
                    onClose={handleClose}
                    allowFullscreen={false}
                />
            )}
        </section>
    );
};

export default ResearchCentreCards;
